/** User-facing date formatting — storage stays ISO/partial strings internally. */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

const YEAR_RE = /^\d{4}$/;
const YEAR_MONTH_RE = /^(\d{4})-(\d{2})$/;
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDateValue(value) {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
      precision: 'day',
    };
  }

  const raw = String(value).trim();
  if (!raw) return null;

  if (YEAR_RE.test(raw)) {
    return { year: Number(raw), precision: 'year' };
  }

  const yearMonth = raw.match(YEAR_MONTH_RE);
  if (yearMonth) {
    const month = Number(yearMonth[2]);
    if (month >= 1 && month <= 12) {
      return { year: Number(yearMonth[1]), month, precision: 'month' };
    }
  }

  const iso = raw.match(ISO_DATE_RE);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { year, month, day, precision: 'day' };
    }
  }

  if (!/^\d{4}/.test(raw)) {
    return { raw, precision: 'raw' };
  }

  return { raw, precision: 'raw' };
}

function monthLabel(month) {
  return MONTHS[month - 1] || '';
}

function formatParsedDate(parsed) {
  if (!parsed) return '';
  if (parsed.precision === 'raw') return parsed.raw;
  if (parsed.precision === 'year') return String(parsed.year);
  if (parsed.precision === 'month') {
    return `${monthLabel(parsed.month)} ${parsed.year}`;
  }
  return `${parsed.day} ${monthLabel(parsed.month)} ${parsed.year}`;
}

/**
 * Format a stored date for display (ISO string, partial date, Date, or null).
 * @returns {string} e.g. "15 Jan 1910", "Sept 1984", "1984", or ""
 */
export function formatDisplayDate(value) {
  return formatParsedDate(parseDateValue(value));
}

/**
 * Format a birth–death range with an en dash.
 * @returns {string} e.g. "15 Jan 1910 – 30 May 1990"
 */
export function formatDisplayDateRange(startDate, endDate) {
  const start = formatDisplayDate(startDate);
  const end = formatDisplayDate(endDate);
  if (start && end) return `${start} – ${end}`;
  return start || end || '';
}

/**
 * Lifespan line for person headers, lists, and memorial pages.
 * @param {string|null|undefined} birthDate
 * @param {string|null|undefined} deathDate
 * @param {{ bornLabel?: boolean, diedLabel?: boolean, rangeOnly?: boolean }} [options]
 */
export function formatLifespan(birthDate, deathDate, options = {}) {
  const {
    bornLabel = true,
    diedLabel = true,
    rangeOnly = false,
  } = options;

  const birth = formatDisplayDate(birthDate);
  const death = formatDisplayDate(deathDate);

  if (birth && death) {
    return `${birth} – ${death}`;
  }
  if (rangeOnly) {
    return birth || death || '';
  }
  if (birth) {
    return bornLabel ? `Born ${birth}` : birth;
  }
  if (death) {
    return diedLabel ? `Died ${death}` : death;
  }
  return '';
}

/** Convenience wrapper for person objects with birth_date / death_date fields. */
export function formatPersonLifespan(person, options) {
  if (!person) return '';
  return formatLifespan(person.birth_date, person.death_date, options);
}

export function formatPersonDateRange(person) {
  if (!person) return '';
  return formatDisplayDateRange(person.birth_date, person.death_date);
}
